/**
 * GIT SETTINGS TAB
 *
 * Two sections:
 * A. Git Identity — per-workspace author name/email for local commits
 * B. Connected Accounts — GitHub App installations + PAT (cloud workspaces only)
 */

import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Building2, User, Key, LogOut, Save, Trash2, Eye, EyeOff } from "lucide-react";
import { useGitConfigStore, type GitConfig } from "@/stores/gitConfigStore";
import { useGitStore } from "@/stores/gitStore";
import type { GitHubInstallation, GitHubPAT } from "@/lib/github-api";
import { isTauri } from "@/hooks/usePlatform";

interface GitSettingsTabProps {
  workspaceId: string;
  backendWorkspaceId: number | null;
}

export function GitSettingsTab({ workspaceId, backendWorkspaceId }: GitSettingsTabProps) {
  return (
    <div className="space-y-6">
      <GitIdentitySection workspaceId={workspaceId} />
      {isTauri() && (
        <>
          <Separator />
          <LocalWorkspacePATSection workspaceId={workspaceId} />
        </>
      )}
      {backendWorkspaceId != null && (
        <>
          <Separator />
          <ConnectedAccountsSection backendWorkspaceId={backendWorkspaceId} />
        </>
      )}
    </div>
  );
}

// ─── Local Workspace PAT ──────────────────────────────────────────────

function LocalWorkspacePATSection({ workspaceId }: { workspaceId: string }) {
  const [token, setToken] = useState("");
  const [showToken, setShowToken] = useState(false);
  const [hasToken, setHasToken] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const secretKey = `gh_pat_ws_${workspaceId}`;

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setIsLoading(true);
      setError(null);
      try {
        const { invoke } = await import("@tauri-apps/api/core");
        const existing = await invoke<string | null>("get_secret", {
          key: secretKey,
        });
        if (!cancelled) {
          setHasToken(!!existing);
        }
      } catch (e: unknown) {
        if (!cancelled) {
          setError(`Failed to load token: ${String(e)}`);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [secretKey]);

  async function handleSaveToken() {
    if (!token.trim()) return;
    setIsSaving(true);
    setError(null);
    try {
      const { invoke } = await import("@tauri-apps/api/core");
      await invoke("set_secret", { key: secretKey, value: token.trim() });
      setToken("");
      setHasToken(true);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e: unknown) {
      setError(`Failed to save token: ${String(e)}`);
    } finally {
      setIsSaving(false);
    }
  }

  async function handleDeleteToken() {
    setIsSaving(true);
    setError(null);
    try {
      const { invoke } = await import("@tauri-apps/api/core");
      await invoke("delete_secret", { key: secretKey });
      setHasToken(false);
      setSaved(false);
    } catch (e: unknown) {
      setError(`Failed to remove token: ${String(e)}`);
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-sm font-medium">GitHub Token</h3>
        <p className="text-xs text-muted-foreground mt-1">
          Used for clone, pull and push in this workspace. Per-project tokens (set on the Git tab) take priority.
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="local-workspace-pat" className="text-xs">
          Personal Access Token
        </Label>
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Input
              id="local-workspace-pat"
              type={showToken ? "text" : "password"}
              placeholder={hasToken ? "Token saved. Enter new token to replace." : "ghp_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"}
              value={token}
              onChange={(e) => {
                setToken(e.target.value);
                setSaved(false);
              }}
              disabled={isLoading || isSaving}
              className="pr-10"
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
              onClick={() => setShowToken((v) => !v)}
              disabled={isLoading || isSaving}
              aria-label={showToken ? "Hide token" : "Show token"}
            >
              {showToken ? (
                <EyeOff className="w-4 h-4 text-muted-foreground" />
              ) : (
                <Eye className="w-4 h-4 text-muted-foreground" />
              )}
            </Button>
          </div>
          <Button
            size="sm"
            onClick={handleSaveToken}
            disabled={!token.trim() || isLoading || isSaving}
          >
            Save Token
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-2">
        {isLoading ? (
          <span className="text-xs text-muted-foreground">Loading token status...</span>
        ) : hasToken ? (
          <Badge variant="outline" className="text-green-600 border-green-600">
            Connected
          </Badge>
        ) : (
          <Badge variant="outline">No token</Badge>
        )}

        {hasToken && (
          <Button
            variant="outline"
            size="sm"
            className="text-destructive hover:text-destructive"
            onClick={handleDeleteToken}
            disabled={isSaving}
          >
            <Trash2 className="w-3.5 h-3.5 mr-1.5" />
            Disconnect
          </Button>
        )}

        {saved && <span className="text-xs text-green-500">Saved</span>}
      </div>

      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}

// ─── Git Identity ────────────────────────────────────────────────────

function GitIdentitySection({ workspaceId }: { workspaceId: string }) {
  const { configs, setConfig, clearConfig } = useGitConfigStore();
  const stored = configs[workspaceId];

  const [name, setName] = useState(stored?.userName ?? "");
  const [email, setEmail] = useState(stored?.userEmail ?? "");
  const [saved, setSaved] = useState(false);

  // Sync when workspace changes
  useEffect(() => {
    const cfg = configs[workspaceId];
    setName(cfg?.userName ?? "");
    setEmail(cfg?.userEmail ?? "");
    setSaved(false);
  }, [workspaceId]);

  const hasChanges =
    name !== (stored?.userName ?? "") || email !== (stored?.userEmail ?? "");
  const bothFilled = name.trim() !== "" && email.trim() !== "";
  const bothEmpty = name.trim() === "" && email.trim() === "";
  const isValid = bothFilled || bothEmpty;

  function handleSave() {
    if (bothEmpty) {
      clearConfig(workspaceId);
    } else if (bothFilled) {
      setConfig(workspaceId, { userName: name.trim(), userEmail: email.trim() });
    }
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  }

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-sm font-medium">Git Identity</h3>
        <p className="text-xs text-muted-foreground mt-1">
          Used as commit author for local Git operations in this workspace.
        </p>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="git-name" className="text-xs">Name</Label>
          <Input
            id="git-name"
            placeholder="Your Name"
            value={name}
            onChange={(e) => { setName(e.target.value); setSaved(false); }}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="git-email" className="text-xs">Email</Label>
          <Input
            id="git-email"
            type="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => { setEmail(e.target.value); setSaved(false); }}
          />
        </div>
      </div>
      {!isValid && (
        <p className="text-xs text-destructive">
          Both name and email must be provided together, or both left empty.
        </p>
      )}
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          disabled={!hasChanges || !isValid}
          onClick={handleSave}
        >
          <Save className="w-3.5 h-3.5 mr-1.5" />
          Save
        </Button>
        {saved && (
          <span className="text-xs text-green-500">Saved</span>
        )}
      </div>
    </div>
  );
}

// ─── Connected Accounts ──────────────────────────────────────────────

function ConnectedAccountsSection({ backendWorkspaceId }: { backendWorkspaceId: number }) {
  const {
    connectedInstallations,
    workspacePAT,
    fetchConnectedInstallations,
    fetchWorkspacePAT,
    disconnectInstallation,
    deletePAT,
  } = useGitStore();

  const [loading, setLoading] = useState(true);
  const [confirmId, setConfirmId] = useState<number | "pat" | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      await Promise.all([
        fetchConnectedInstallations(backendWorkspaceId),
        fetchWorkspacePAT(backendWorkspaceId),
      ]);
      if (!cancelled) setLoading(false);
    }
    load();
    return () => { cancelled = true; };
  }, [backendWorkspaceId]);

  async function handleDisconnectInstallation(installationDbId: number) {
    await disconnectInstallation(backendWorkspaceId, installationDbId);
    setConfirmId(null);
  }

  async function handleDeletePAT() {
    await deletePAT(backendWorkspaceId);
    setConfirmId(null);
  }

  const hasAnything = connectedInstallations.length > 0 || workspacePAT;

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-sm font-medium">Connected Accounts</h3>
        <p className="text-xs text-muted-foreground mt-1">
          GitHub accounts connected to this workspace for repository access.
        </p>
      </div>

      {loading && (
        <p className="text-xs text-muted-foreground">Loading...</p>
      )}

      {!loading && !hasAnything && (
        <p className="text-xs text-muted-foreground">
          No GitHub accounts connected. Connect via the Git tab on a project.
        </p>
      )}

      {/* Installations */}
      {connectedInstallations.map((inst) => (
        <InstallationRow
          key={inst.id}
          installation={inst}
          isConfirming={confirmId === inst.id}
          onConfirmStart={() => setConfirmId(inst.id)}
          onConfirmCancel={() => setConfirmId(null)}
          onDisconnect={() => handleDisconnectInstallation(inst.id)}
        />
      ))}

      {/* PAT */}
      {workspacePAT && (
        <PATRow
          pat={workspacePAT}
          isConfirming={confirmId === "pat"}
          onConfirmStart={() => setConfirmId("pat")}
          onConfirmCancel={() => setConfirmId(null)}
          onDelete={handleDeletePAT}
        />
      )}
    </div>
  );
}

// ─── Sub-components ──────────────────────────────────────────────────

function InstallationRow({
  installation,
  isConfirming,
  onConfirmStart,
  onConfirmCancel,
  onDisconnect,
}: {
  installation: GitHubInstallation;
  isConfirming: boolean;
  onConfirmStart: () => void;
  onConfirmCancel: () => void;
  onDisconnect: () => void;
}) {
  return (
    <div className="flex items-center gap-3 p-3 border rounded-lg bg-muted/50">
      {installation.accountAvatarUrl ? (
        <img
          src={installation.accountAvatarUrl}
          alt={installation.accountLogin}
          className="w-8 h-8 rounded-full"
        />
      ) : installation.accountType === "Organization" ? (
        <Building2 className="w-8 h-8 text-muted-foreground" />
      ) : (
        <User className="w-8 h-8 text-muted-foreground" />
      )}
      <div className="flex-1 min-w-0">
        <p className="font-medium text-sm truncate">{installation.accountLogin}</p>
        <p className="text-xs text-muted-foreground">
          {installation.accountType} · GitHub App
        </p>
      </div>
      {isConfirming ? (
        <div className="flex items-center gap-2">
          <span className="text-xs text-destructive">Disconnect?</span>
          <Button variant="destructive" size="sm" onClick={onDisconnect}>
            Yes
          </Button>
          <Button variant="ghost" size="sm" onClick={onConfirmCancel}>
            No
          </Button>
        </div>
      ) : (
        <Button
          variant="outline"
          size="sm"
          className="text-destructive hover:text-destructive"
          onClick={onConfirmStart}
        >
          <LogOut className="w-3.5 h-3.5 mr-1.5" />
          Disconnect
        </Button>
      )}
    </div>
  );
}

function PATRow({
  pat,
  isConfirming,
  onConfirmStart,
  onConfirmCancel,
  onDelete,
}: {
  pat: GitHubPAT;
  isConfirming: boolean;
  onConfirmStart: () => void;
  onConfirmCancel: () => void;
  onDelete: () => void;
}) {
  return (
    <div className="flex items-center gap-3 p-3 border rounded-lg bg-muted/50">
      <Key className="w-8 h-8 text-muted-foreground" />
      <div className="flex-1 min-w-0">
        <p className="font-medium text-sm truncate">
          {pat.githubLogin ? `@${pat.githubLogin}` : "Personal Access Token"}
        </p>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          {pat.tokenHint && <span>{pat.tokenHint}</span>}
          {pat.scopes.length > 0 && (
            <div className="flex gap-1">
              {pat.scopes.slice(0, 3).map((s) => (
                <Badge key={s} variant="outline" className="text-[10px] px-1 py-0">
                  {s}
                </Badge>
              ))}
              {pat.scopes.length > 3 && (
                <span>+{pat.scopes.length - 3}</span>
              )}
            </div>
          )}
        </div>
      </div>
      {isConfirming ? (
        <div className="flex items-center gap-2">
          <span className="text-xs text-destructive">Remove?</span>
          <Button variant="destructive" size="sm" onClick={onDelete}>
            Yes
          </Button>
          <Button variant="ghost" size="sm" onClick={onConfirmCancel}>
            No
          </Button>
        </div>
      ) : (
        <Button
          variant="outline"
          size="sm"
          className="text-destructive hover:text-destructive"
          onClick={onConfirmStart}
        >
          <Trash2 className="w-3.5 h-3.5 mr-1.5" />
          Remove Token
        </Button>
      )}
    </div>
  );
}
